<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" elevation="0" :title="$props.title">
      <v-row no-gutters>
        <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
          <v-sheet class="pa-2 ma-2">
            <div class="number-box-filtered">
              <transition name="fade-bounce" mode="out-in">
                <div :key="filteredDemographicBaselineTotalNumberOfPatients.value">
                  <span class="large-number">{{ filteredDemographicBaselineTotalNumberOfPatients.value }}</span>
                  <span class="percentage-number" v-if="filteredDemographicBaselineTotalNumberOfPatients.percentage">&nbsp;
                            {{ filteredDemographicBaselineTotalNumberOfPatients.percentage }}%
                          </span>
                </div>
              </transition>
              <div class="label large-number-label">{{ filteredDemographicBaselineTotalNumberOfPatients.label }}</div>
              <div class="label large-number-comment">{{ filteredDemographicBaselineTotalNumberOfPatients.comment }}</div>
            </div>
          </v-sheet>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4" align-self="end">
          <v-select class="mr-5" v-model="selected_stratify_items" label="Comparison groups"
                    :items="select_stratify_items" :multiple="true">
            <template #item="{ props }">
              <v-list-subheader v-if="props.header">{{ props.header }}</v-list-subheader>
              <v-divider v-else-if="props.divider" class="mt-2" />
              <v-list-item v-else v-bind="props" class="ml-2"></v-list-item>
            </template>
          </v-select>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <v-card class="mx-auto" min-height="800" elevation="0">
            <GChart type="ColumnChart" :data="trendsChartData"
                    :options="trendsChartOptions"
                    v-if="trendsChartData.length !== 0"/>
            <v-chip variant="elevated" size="x-large" density="default" v-else>
              No data to display. Please adjust your filters.
            </v-chip>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";

export default defineComponent({
  name: 'SocialstyrelsenGenericTrends',
  props: {
    title: String,
    trendsUrl: String,
    trendsHorizontalAxisLabel: String,
    yearGroupIncluded: Boolean,
    regionGroupIncluded: Boolean,
    icd_or_atc: Array,
    dataset: {},
    measurementType: {},
    ageAtDiagnosis: Array,
    diagnosisDate: Array,
    sex: Array,
    region: Array,
    datasets: Array,
    visible: Boolean,
    perX: {
      type: Number,
      default: undefined
    }
  },
  components: {
    GChart
  },
  data: context => ({
    select_stratify_items: [],
    selected_stratify_items: [],
    filteredDemographicBaselineTotalNumberOfPatients: {},
    trendsChartData: [],
    trendsChartOptions: {
      title: '',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7'],
      legend: {
        position: 'bottom',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      series: {
        0: {
          targetAxisIndex: 0,
          type: 'line'
        },
        1: {
          targetAxisIndex: 0
        }
      },
      hAxis: {
        title: context.trendsHorizontalAxisLabel,
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff'
        }
      },
      vAxis: {
        title: '',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff'
        }
      },
      tooltip: {
        isHtml: true
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    }
  }),
  watch: {
    selected_stratify_items() {
      this.loadDynamicData()
    }
  },
  methods: {
    updateSelectStratifyItems() {
      let updated_select_stratify_items = []
      let filtered_datasets = this.datasets
          .filter(dataset => dataset.value !== this.dataset.value)
      if (filtered_datasets.length > 0) {
        updated_select_stratify_items.push({
          props: {header: 'Patient selection'}
        })
        filtered_datasets
            .forEach(element => updated_select_stratify_items.push({
              title: element.title,
              value: element.value
            }))
        updated_select_stratify_items.push({
          props: {divider: true}
        })
      }
      updated_select_stratify_items.push({
        props: { header: 'Sex' }
      })
      this.sex.forEach(element =>
        updated_select_stratify_items.push({
          title: element
        }))
      updated_select_stratify_items.push({
        props: { divider: true }
      })
      updated_select_stratify_items.push({
        props: {header: 'Age'}
      })
      this.ageAtDiagnosis.forEach(element =>
          updated_select_stratify_items.push({
            title: element
          }))
      if (this.regionGroupIncluded) {
        updated_select_stratify_items.push({
          props: { divider: true }
        })
        updated_select_stratify_items.push({
          props: { header: 'Region' }
        })
        this.region.forEach(element =>
            updated_select_stratify_items.push({
              title: element
            }))
      }
      if (this.yearGroupIncluded) {
        updated_select_stratify_items.push({
          props: { divider: true }
        })
        updated_select_stratify_items.push({
          props: { header: 'Year' }
        })
        this.diagnosisDate.forEach(element =>
            updated_select_stratify_items.push({
              title: element
            }))
      }

      this.select_stratify_items = updated_select_stratify_items
    },
    updateSelectedStratifyItems() {
      let permittedDatasets = this.datasets
          .filter(dataset => dataset.value !== this.dataset.value)
          .map(dataset => dataset.value)
      this.selected_stratify_items = this.selected_stratify_items.filter(element => {
        return permittedDatasets.includes(element) ||
            this.ageAtDiagnosis.includes(element) || this.diagnosisDate.includes(element) ||
            this.sex.includes(element) || this.region.includes(element)
      })
    },
    async loadFilteredDemographicBaselineTotalNumberOfPatientsData() {
      return axios.get('/socialstyrelsen-generic/filtered-demographic-baseline-total-number',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds_or_atcs: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              measurement_type: this.measurementType.value,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              sex: this.sex.join(","),
              region: this.region.join(","),
              per_x: this.perX
            }
          });
    },
    async loadTrendsData() {
      this.trendsChartOptions.vAxis.title = this.measurementType.title + ((this.perX !== undefined) ? ' per ' +
          this.perX.toLocaleString() + ' inhabitants' : '')
      return axios.get(this.trendsUrl,
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icds: this.icd_or_atc.join(','),
              dataset: this.dataset.value,
              measurement_type: this.measurementType.value,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              sex: this.sex.join(","),
              region: this.region.join(","),
              per_x: this.perX,
              stratify_items: this.selected_stratify_items.join(",")
            }
          });
    },
    async loadDynamicData() {
      this.updateSelectStratifyItems()
      await Promise.all([
        this.loadFilteredDemographicBaselineTotalNumberOfPatientsData(),
        this.loadTrendsData()
      ]).then(responses => {
        this.filteredDemographicBaselineTotalNumberOfPatients = responses[0].data
        this.trendsChartData = responses[1].data
        console.log('Dynamic data loaded SocialstyrelsenGenericTrends')
      })
    }
  },
  mounted() {
    this.loadDynamicData()
  },
  updated() {
    if (this.visible) {
      this.updateSelectedStratifyItems()
    }
  }
});
</script>

<style scoped>
.number-box-filtered {
  height: 250px;
  justify-content: space-around;
  text-align: center;
  padding: 50px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.large-number {
  font-size: 4em;
  font-weight: bold;
}

.percentage-number {
  font-size: 2em;
  color: #b2df8a;
}

.large-number-label {
  font-size: 1em;
  margin-top: 10px;
}

.large-number-comment {
  font-size: 0.8em;
  margin-top: 10px;
  margin-left: 20px;
  margin-right: 20px;
}

.fade-bounce-enter-active {
  transition: all 0.5s ease-in;
  animation: bounce-in 0.5s;
}
.fade-bounce-leave-active {
  transition: all 0.5s ease-out;
  animation: bounce-in 0.5s reverse;
}

.fade-bounce-enter-from,
.fade-bounce-leave-to {
  opacity: 0;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
}
</style>
